import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, ShieldCheck, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = ["New Jersey", "Delaware", "Pennsylvania"];
const SERVICES = [
  "Seamless Gutters",
  "Gutter Guards",
  "Downspout Extensions",
  "Basement Waterproofing",
  "Full System Shield",
];

const EMPTY = { full_name: "", phone: "", email: "", state: "", service: "", notes: "" };

const inputCls =
  "w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm text-slate-100 placeholder-slate-500 transition-colors focus:border-orange-500 focus:outline-none";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
      {label}
    </label>
    {children}
  </div>
);

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Request received — we'll call you within one business day.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" className="border-t border-white/10 bg-ink2/50 py-20 lg:py-32" data-testid="quote-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <p className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.3em] text-orange-500">
            Free Inspection &amp; Quote
          </p>
          <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.95] text-white sm:text-5xl lg:text-6xl">
            Lock In Your <span className="text-orange-500">Dry Season</span>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400">
            Tell us what's going on — overflow, basement damp, foundation
            cracks — and we'll schedule a free on-site inspection. Written
            quote within 24 hours, good for 30 days.
          </p>
          <div className="mt-10 space-y-5">
            <a href="tel:+19089567550" className="flex items-center gap-4 text-slate-200 transition-colors hover:text-orange-500" data-testid="quote-phone-link">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500/10">
                <Phone className="h-5 w-5 text-orange-500" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-slate-500">Call or text</span>
                <span className="font-display text-xl font-bold">(908) 956-7550</span>
              </span>
            </a>
            <a href="mailto:operations@dnghsolutions.com" className="flex items-center gap-4 text-slate-200 transition-colors hover:text-orange-500" data-testid="quote-email-link">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500/10">
                <Mail className="h-5 w-5 text-orange-500" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-slate-500">Email</span>
                <span className="font-display text-xl font-bold">operations@dnghsolutions.com</span>
              </span>
            </a>
            <div className="flex items-center gap-4 text-slate-200">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500/10">
                <ShieldCheck className="h-5 w-5 text-orange-500" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-slate-500">Licensed &amp; Insured</span>
                <span className="font-display text-xl font-bold">NJ · DE · PA</span>
              </span>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            className="rounded-2xl border border-white/10 bg-ink p-6 sm:p-8"
            data-testid="free-quote-form"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Full Name" htmlFor="q-name">
                <input id="q-name" required value={form.full_name} onChange={set("full_name")} placeholder="Jane Rivera" className={inputCls} data-testid="quote-input-name" />
              </Field>
              <Field label="Phone Number" htmlFor="q-phone">
                <input id="q-phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="(609) 555-0100" className={inputCls} data-testid="quote-input-phone" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Email Address" htmlFor="q-email">
                  <input id="q-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@email.com" className={inputCls} data-testid="quote-input-email" />
                </Field>
              </div>
              <Field label="State" htmlFor="q-state">
                <select id="q-state" required value={form.state} onChange={set("state")} className={inputCls} data-testid="quote-select-state">
                  <option value="">Select state</option>
                  {STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Service Needed" htmlFor="q-service">
                <select id="q-service" required value={form.service} onChange={set("service")} className={inputCls} data-testid="quote-select-service">
                  <option value="">Select service</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Project Details / Issues" htmlFor="q-notes">
                  <textarea id="q-notes" rows={4} value={form.notes} onChange={set("notes")} placeholder="e.g. Gutters overflow at the back corner, basement smells damp after heavy rain…" className={inputCls} data-testid="quote-input-notes" />
                </Field>
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-orange-500 px-7 py-4 text-sm font-bold uppercase tracking-wide text-ink transition-all hover:-translate-y-0.5 hover:bg-orange-600 hover:text-white disabled:opacity-60"
              data-testid="quote-submit-button"
            >
              {sending && <Loader2 className="h-4 w-4 animate-spin" />}
              {sending ? "Sending…" : "Submit Free Inspection Request"}
            </button>
            <p className="mt-4 text-center text-xs text-slate-500">
              No spam, no robo-calls. One human calls you back.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
